// UI strings for "Sarjan äänet" (Kuuntele → the show-audio capture tool) and the label of a show
// clip wherever it plays. Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md. A show clip is always "oma tallenne" (the users' own recording),
// never official.
import { defineArea } from '../area';

const fiNum = (n: number, digits = 0) => n.toLocaleString('fi-FI', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const enNum = (n: number, digits = 0) => n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const mbFi = (b: number) => `${fiNum(b / 1e6, 1)} Mt`;
const mbEn = (b: number) => `${enNum(b / 1e6, 1)} MB`;

export const showAudioFi = {
  title: 'Sarjan äänet',
  door: 'Sarjan äänet: tallenna repliikit jaksoa katsoessa',
  /** The label of a show clip everywhere it plays. */
  label: 'sarjan ääni · oma tallenne',
  /**
   * Beside the label: an actor's pronunciation, not a model (the actors do not always say High
   * Valyrian right). Show clips are listening material only; Peterson stays the reference.
   */
  actor: 'näyttelijän ääntämys · sarjasta',
  /** Short tag on a line that has a show clip. */
  has: 'sarjan ääni',
  shows: { got: 'Game of Thrones', hotd: 'House of the Dragon', akotsk: 'A Knight of the Seven Kingdoms' } as Record<string, string>,
  pick: {
    h: 'Jakso',
    show: 'Sarja',
    season: 'Kausi',
    episode: 'Jakso',
    seasonN: (n: number) => `Kausi ${n}`,
    episodeN: (n: number) => `Jakso ${n}`,
    lines: (n: number) => (n === 1 ? '1 repliikki' : `${n} repliikkiä`),
    have: (n: number, of: number) => `sarjan ääni ${n}/${of}`,
    loading: 'Valmistellaan sanakirjaa…',
    empty: 'Jaksoille ei löytynyt valyriankielisiä repliikkejä.',
  },
  lines: {
    h: 'Jakson repliikit',
    scene: (n: number) => `Kohtaus ${n}`,
    jump: 'Merkitse seuraavaksi',
  },
  input: {
    h: 'Äänilähde',
    label: 'Äänilähde',
    allow: 'Salli mikrofoni',
    help: 'Valitse mikrofoni, joka kuulee television tai kaiuttimet, esimerkiksi kannettavan tai puhelimen mikrofoni kaiuttimen lähellä. Jos käyttöjärjestelmä tarjoaa tietokoneen oman äänen tulona (esim. Stereomiksaus), voit valita sen. Pidä huone hiljaisena.',
    unnamed: (n: number) => `Äänilähde ${n}`,
    level: 'Äänen taso',
    quiet: 'Ääntä ei kuulu. Tarkista äänilähde ja äänenvoimakkuus.',
    loud: 'Ääni säröytyy. Hiljennä tai siirrä mikrofoni kauemmas.',
    denied: 'Mikrofonin käyttö on estetty. Salli se selaimen osoiterivin asetuksista ja yritä uudelleen.',
    unsupported: 'Tämä selain ei pysty tallentamaan. Käytä tietokoneella Chromea, Edgeä tai Firefoxia.',
    failed: 'Äänilähdettä ei saatu auki. Valitse toinen.',
  },
  session: {
    start: 'Aloita tallennus',
    keys: 'Välilyönti: repliikki alkaa nyt · Esc: kumoa · →: ohita · P: tauko',
    markNow: 'Repliikki alkaa nyt',
    undo: 'Kumoa',
    skip: 'Ohita repliikki',
    pause: 'Tauko',
    resume: 'Jatka',
    stop: 'Lopeta ja tarkista',
    now: 'Seuraava repliikki',
    after: 'Sen jälkeen',
    allMarked: 'Jakson kaikki repliikit on merkitty. Lopeta, kun viimeinen on kuultu.',
    recording: 'Tallentaa',
    paused: 'Tauolla',
    marked: (n: number, of: number) => `Merkitty ${n}/${of}`,
    stored: (bytes: number) => `${mbFi(bytes)} laitteella`,
    lastMark: (who: string) => `Merkitty: ${who}`,
    storeFailed: 'Tallennus laitteelle epäonnistui. Lopeta ja tarkista, mitä ehti tallentua.',
    rateMismatch: 'Tätä tallennetta ei voi jatkaa tällä äänilähteellä. Tarkista se ja aloita uusi.',
  },
  recover: {
    h: 'Keskeneräinen tallenne',
    row: (ep: string, marks: number, when: string) => `${ep} · ${marks === 1 ? '1 merkintä' : `${marks} merkintää`} · ${when}`,
    review: 'Tarkista leikkeet',
    resume: 'Jatka tallennusta',
    discard: 'Poista laitteelta',
    confirm: 'Poistetaanko tallenne ja sen merkinnät laitteelta?',
    yes: 'Poista',
  },
  review: {
    h: 'Tarkista leikkeet',
    preparing: (n: number, of: number) => `Rajataan leikkeitä ${n}/${of}…`,
    none: 'Yhtään repliikkiä ei merkitty. Palaa tallentamaan tai poista tallenne.',
    play: 'Toista',
    stop: 'Pysäytä',
    start: 'Alku',
    end: 'Loppu',
    earlier: '−50 ms',
    later: '+50 ms',
    earlierLabel: (edge: string) => `${edge} 50 ms aiemmin`,
    laterLabel: (edge: string) => `${edge} 50 ms myöhemmin`,
    length: (s: number) => `${fiNum(s, 2)} s`,
    line: 'Repliikki',
    discard: 'Hylkää',
    restore: 'Palauta',
    discarded: 'hylätty',
    method: { aligned: 'rajattu tavujen mukaan', energy: 'rajattu hiljaisuuden mukaan', mark: 'rajaus epävarma, kuuntele' } as Record<
      'aligned' | 'energy' | 'mark',
      string
    >,
    sent: 'tallennettu',
    save: (n: number) => (n === 1 ? 'Tallenna 1 leike' : `Tallenna ${n} leikettä`),
    saving: (n: number, of: number) => `Tallennetaan ${n}/${of}…`,
    savedAll: (n: number) =>
      n === 1 ? '1 leike tallennettu. Se soi nyt kuuntelussa ja kohtauksissa.' : `${n} leikettä tallennettu. Ne soivat nyt kuuntelussa ja kohtauksissa.`,
    failed: 'Tallennus keskeytyi. Yritä uudelleen: jo tallennetut leikkeet eivät tallennu toista kertaa.',
    offline: 'Ei yhteyttä. Leikkeet odottavat tällä laitteella; tallenna, kun yhteys palaa.',
    demo: 'Esittelytilassa leikkeitä ei tallenneta.',
    continueRec: 'Jatka tallennusta',
    finish: 'Valmis, poista tallenne laitteelta',
    keys: 'Toista leike napauttamalla. Kuuntele erityisesti leikkeet, joiden rajaus on epävarma.',
  },
  prefer: {
    label: 'Kun repliikillä on kaksi ääntä',
    peterson: 'Petersonin',
    show: 'Sarjan ääni',
  },
  back: 'Kuuntele',
  /** Kuuntele → Äänet laitteelle: the show clips go along with the other recordings. */
  offlineHave: (n: number, of: number) =>
    n === of
      ? of === 1
        ? 'Ainoa sarjasta tallentamanne leike on tällä laitteella.'
        : `Kaikki ${of} sarjasta tallentamaanne leikettä ovat tällä laitteella.`
      : `Sarjasta tallentamianne leikkeitä tällä laitteella ${n}/${of}.`,
};

export const showAudioEn: typeof showAudioFi = {
  title: 'Show voices',
  door: 'Show voices: record the lines while you watch',
  label: 'show voice · own recording',
  actor: 'actor’s pronunciation · from the show',
  has: 'show voice',
  shows: { got: 'Game of Thrones', hotd: 'House of the Dragon', akotsk: 'A Knight of the Seven Kingdoms' },
  pick: {
    h: 'Episode',
    show: 'Show',
    season: 'Season',
    episode: 'Episode',
    seasonN: (n: number) => `Season ${n}`,
    episodeN: (n: number) => `Episode ${n}`,
    lines: (n: number) => (n === 1 ? '1 line' : `${n} lines`),
    have: (n: number, of: number) => `show voice ${n}/${of}`,
    loading: 'Preparing the dictionary…',
    empty: 'No High Valyrian lines were found for the episodes.',
  },
  lines: {
    h: 'Lines of the episode',
    scene: (n: number) => `Scene ${n}`,
    jump: 'Mark this one next',
  },
  input: {
    h: 'Audio input',
    label: 'Audio input',
    allow: 'Allow the microphone',
    help: 'Pick a microphone that hears the TV or the speakers, for example a laptop or phone microphone near a speaker. If your system offers the computer’s own sound as an input (e.g. Stereo Mix), you can pick that. Keep the room quiet.',
    unnamed: (n: number) => `Audio input ${n}`,
    level: 'Input level',
    quiet: 'Nothing can be heard. Check the input and the volume.',
    loud: 'The sound is distorting. Turn it down or move the microphone further away.',
    denied: 'Microphone access is blocked. Allow it in the browser’s address bar settings and try again.',
    unsupported: 'This browser cannot record. On a computer, use Chrome, Edge or Firefox.',
    failed: 'The input could not be opened. Pick another one.',
  },
  session: {
    start: 'Start recording',
    keys: 'Space: the line starts now · Esc: undo · →: skip · P: pause',
    markNow: 'The line starts now',
    undo: 'Undo',
    skip: 'Skip the line',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop and check',
    now: 'Next line',
    after: 'After that',
    allMarked: 'Every line of the episode is marked. Stop once the last one has been heard.',
    recording: 'Recording',
    paused: 'Paused',
    marked: (n: number, of: number) => `Marked ${n}/${of}`,
    stored: (bytes: number) => `${mbEn(bytes)} on this device`,
    lastMark: (who: string) => `Marked: ${who}`,
    storeFailed: 'Saving to this device failed. Stop and check what was recorded.',
    rateMismatch: 'This recording cannot continue with this input. Check it and start a new one.',
  },
  recover: {
    h: 'Unfinished recording',
    row: (ep: string, marks: number, when: string) => `${ep} · ${marks === 1 ? '1 mark' : `${marks} marks`} · ${when}`,
    review: 'Check the clips',
    resume: 'Continue recording',
    discard: 'Remove from this device',
    confirm: 'Remove the recording and its marks from this device?',
    yes: 'Remove',
  },
  review: {
    h: 'Check the clips',
    preparing: (n: number, of: number) => `Trimming clips ${n}/${of}…`,
    none: 'No lines were marked. Go back to recording or remove the recording.',
    play: 'Play',
    stop: 'Stop',
    start: 'Start',
    end: 'End',
    earlier: '−50 ms',
    later: '+50 ms',
    earlierLabel: (edge: string) => `${edge} 50 ms earlier`,
    laterLabel: (edge: string) => `${edge} 50 ms later`,
    length: (s: number) => `${enNum(s, 2)} s`,
    line: 'Line',
    discard: 'Discard',
    restore: 'Restore',
    discarded: 'discarded',
    method: { aligned: 'trimmed to the syllables', energy: 'trimmed at the silences', mark: 'unsure edges, listen' },
    sent: 'saved',
    save: (n: number) => (n === 1 ? 'Save 1 clip' : `Save ${n} clips`),
    saving: (n: number, of: number) => `Saving ${n}/${of}…`,
    savedAll: (n: number) =>
      n === 1 ? '1 clip saved. It now plays in listening and in the scenes.' : `${n} clips saved. They now play in listening and in the scenes.`,
    failed: 'Saving stopped. Try again: clips already saved are not saved twice.',
    offline: 'Offline. The clips wait on this device; save them when you are back online.',
    demo: 'Clips are not saved in the demo.',
    continueRec: 'Continue recording',
    finish: 'Done, remove the recording from this device',
    keys: 'Tap a clip to play it. Listen especially to clips with unsure edges.',
  },
  prefer: {
    label: 'When a line has two voices',
    peterson: 'Peterson’s',
    show: 'Show voice',
  },
  back: 'Listen',
  offlineHave: (n, of) =>
    n === of
      ? of === 1
        ? 'Your only show clip is on this device.'
        : `All ${of} of your show clips are on this device.`
      : `${n}/${of} of your show clips on this device.`,
};

// Messages.showAudio (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('showAudio', showAudioFi, showAudioEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof showAudioFi> {}
}
